/* Shared, side-effect-free gift eligibility for cart page and drawer. */
(function (scope) {
  'use strict';
  const ATTR = { rule: '_zyf_gift_rule', tier: '_zyf_gift_tier', group: '_zyf_gift_group', revision: '_zyf_gift_revision' };
  const DEFERRED = '_zyf_gift_deferred';
  const suffix = value => String(value || '').split('/').pop();
  const normal = value => String(value || '').trim().toLowerCase();
  const managed = line => Object.prototype.hasOwnProperty.call(line.properties || {}, ATTR.rule);
  const price = line => Math.max(0, Number(line.final_line_price ?? line.line_price ?? 0));

  // Match the known insurance SKU only; other Seel products remain ordinary items.
  const external = line => line.sku === 'SEEL-WFP';
  const identity = line => JSON.stringify([
    external(line) ? 'sku:SEEL-WFP' : suffix(line.variant_id ?? line.variantId),
    Object.entries(line.properties || {}).sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0),
    suffix(line.selling_plan_allocation?.selling_plan?.id ?? line.sellingPlanId ?? line.selling_plan)
  ]);
  const quantities = lines => {
    const totals = new Map();
    for (const line of lines) {
      const id = identity(line);
      totals.set(id, (totals.get(id) || 0) + line.quantity);
    }
    return JSON.stringify([...totals].sort(([a], [b]) => a < b ? -1 : a > b ? 1 : 0));
  };
  const fingerprint = cart => quantities(cart.items.filter(line => !managed(line) && !external(line)));
  const selectedLines = (cart, expected) => {
    if (!expected.length || expected.some(line => !Number.isInteger(line.quantity) || line.quantity <= 0)) {
      throw new Error('Please select items to checkout.');
    }
    const ids = new Set(expected.map(identity));
    const lines = cart.items.filter(line => !managed(line) && ids.has(identity(line)));
    // Ambiguous partial selection of identical split lines must be reconfirmed.
    if (quantities(lines) !== quantities(expected)) {
      throw new Error('Your selected items changed. Please refresh and select your items again.');
    }
    return lines;
  };

  function matches(line, eligibility, facts) {
    if (managed(line)) return false;
    const fact = facts.find(item => String(item.variantId) === String(line.variant_id)) || {};
    const collection = (eligibility.collectionIds || []).some(id => (fact.collectionIds || []).map(suffix).includes(suffix(id)));
    const type = (eligibility.productTypes || []).map(normal).includes(normal(line.product_type || fact.productType));
    const tag = (eligibility.tags || []).map(normal).some(value => (fact.tags || []).map(normal).includes(value));
    if (eligibility.productIds?.length || eligibility.titleKeywords?.length) {
      return (!eligibility.productIds?.length || eligibility.productIds.map(suffix).includes(String(line.product_id || fact.productId))) &&
        (!eligibility.collectionIds?.length || collection) && (!eligibility.productTypes?.length || type) &&
        (!eligibility.tags?.length || tag) && (!eligibility.titleKeywords?.length || eligibility.titleKeywords.some(word => normal(line.product_title || fact.title).includes(normal(word))));
    }
    return (!eligibility.collectionIds?.length && !eligibility.productTypes?.length && !eligibility.tags?.length) || collection || type || tag;
  }

  function amount(rule, cart, facts, rate = 1, keys) {
    const ordinary = cart.items.filter(line => !managed(line) && !external(line) && (!keys || keys.has(line.key)));
    const matched = ordinary.filter(line => matches(line, rule.eligibility, facts));
    if (!matched.length) return 0;
    const scoped = rule.eligibility.subtotalScope === 'MATCHED_ITEMS' ? matched : ordinary;
    const cents = !keys && rule.eligibility.subtotalScope !== 'MATCHED_ITEMS' && cart.total_price != null
      ? Math.max(0, cart.total_price - cart.items.filter(line => managed(line) || external(line)).reduce((sum, line) => sum + price(line), 0))
      : scoped.reduce((sum, line) => sum + price(line), 0);
    return cents / 100 / rate;
  }

  function resolve(config, cart, facts, stock, choices, rate = 1, keys, allows = () => true) {
    if (scope?.KateGiftSafety?.suppressed) return null;
    const groups = new Map();
    for (const rule of config.rules || []) {
      if (!allows(rule)) continue;
      const subtotal = amount(rule, cart, facts, rate, keys);
      const eligible = [...rule.tiers].filter(tier => subtotal >= Number(tier.threshold)).sort((a, b) => b.threshold - a.threshold);
      if (!eligible.length) continue;
      const current = groups.get(rule.group);
      if (current && Number(current.rule.priority) >= Number(rule.priority)) continue;
      let reward = null;
      for (const tier of eligible) {
        const available = tier.candidates.filter(candidate => stock.get(suffix(candidate.variantId)) === true);
        const selected = available.find(candidate => suffix(candidate.variantId) === choices.get(`${rule.id}:${tier.id}`)) || available[0];
        if (selected) { reward = { rule, tier, candidate: selected, subtotal }; break; }
        if (tier.candidates.some(candidate => stock.get(suffix(candidate.variantId)) == null)) break;
      }
      groups.set(rule.group, { rule, subtotal, reward });
    }
    // The current application enables one activity; keep exactly one gift even if an older projection contains several groups.
    return [...groups.values()].sort((a, b) => Number(b.rule.priority) - Number(a.rule.priority))[0]?.reward || null;
  }

  function properties(config, reward) {
    return { [ATTR.rule]: reward.rule.id, [ATTR.tier]: reward.tier.id, [ATTR.group]: reward.rule.group, [ATTR.revision]: String(config.revision) };
  }
  const rewardLine = (config, line, reward) => String(line.variant_id) === suffix(reward.candidate.variantId) &&
    Object.entries(properties(config, reward)).every(([key, value]) => line.properties?.[key] === value);

  function giftState(config, cart, line, facts, stock, choices, rate = 1, keys, allows = () => true) {
    const rule = config.rules?.find(item => item.id === line.properties?.[ATTR.rule]);
    const tier = rule?.tiers.find(item => item.id === line.properties?.[ATTR.tier]);
    const candidate = tier?.candidates.find(item => suffix(item.variantId) === String(line.variant_id));
    if (!rule || !tier || !candidate || !allows(rule) || line.quantity !== 1 ||
      !rewardLine(config, line, { rule, tier, candidate }) || amount(rule, cart, facts, rate, keys) < Number(tier.threshold)) return 'invalid';
    if (line.final_line_price == null && line.line_price == null) return 'unknown';
    if (price(line) !== 0 || stock.get(String(line.variant_id)) === false) return 'invalid';
    const reward = resolve(config, cart, facts, stock, choices, rate, keys, allows);
    if (reward && (reward.rule.id !== rule.id || reward.tier.id !== tier.id)) return 'invalid';
    if (stock.get(String(line.variant_id)) == null || !reward) return 'unknown';
    return rewardLine(config, line, reward) ? 'valid' : 'invalid';
  }

  const core = { ATTR, DEFERRED, suffix, managed, external, identity, fingerprint, selectedLines, matches, amount, resolve, properties, giftState };
  if (typeof module !== 'undefined' && module.exports) module.exports = core;
  if (scope) scope.KateCartGiftCore = core;
})(typeof window !== 'undefined' ? window : null);
